// ─────────────────────────────────────────────────────────────
// Current user resolver
//
// iter168 (med fix): the `window.ABS_AUTH` lookup that this file
// previously consulted was a spoofing surface — any script (a stray
// browser extension, a copy-pasted devtools snippet, a postMessage
// from an embedded iframe) could overwrite it and impersonate any
// user, then the change-bearing fields on Proposals / WOs / Leads
// would attribute work to the spoofed identity. The JWT cookie set
// by /api/auth/login is the real source of truth — AuthContext
// keeps `abs_user_email` in localStorage in sync after every login,
// so that's the only fallback we need here.
//
// Source priority:
//   1. localStorage.abs_user_email  (synced by AuthContext on login)
//   2. empty (no user)
//
// The email is the source of truth. The display name is looked up
// from the AM roster in Settings so adding/removing Account Managers
// is the single source of truth for authorization + autofill.
// ─────────────────────────────────────────────────────────────
import { getAccountManagers } from "./api";

let _amCache = null;
let _amCachePromise = null;

const fetchAMs = async () => {
  if (_amCache) return _amCache;
  if (_amCachePromise) return _amCachePromise;
  _amCachePromise = getAccountManagers()
    .then(r => {
      _amCache = r.data || [];
      return _amCache;
    })
    .catch(() => { _amCache = []; return []; });
  return _amCachePromise;
};

export const invalidateAMCache = () => { _amCache = null; _amCachePromise = null; };

export const getCurrentUserEmail = () => {
  // #(med) (iter168): NEVER read window.ABS_AUTH — it's a spoofing
  // surface. AuthContext writes the JWT-derived email to
  // `abs_user_email` on every successful login + refresh.
  return localStorage.getItem("abs_user_email") || "";
};

export const setCurrentUserEmail = (email) => {
  if (!email) {
    localStorage.removeItem("abs_user_email");
    // Clear derived/legacy keys too so stale AM name doesn't leak
    localStorage.removeItem("abs_current_user");
    localStorage.removeItem("abs_current_user_email");
    localStorage.removeItem("abs_prepared_by");
    localStorage.removeItem("abs_prepared_by_email");
  } else {
    localStorage.setItem("abs_user_email", email.trim().toLowerCase());
  }
  window.dispatchEvent(new Event("abs-user-changed"));
};

// Resolve {name, email, isAM} from the current user's email by matching the AM list.
// Returns null when no user is set.
export const resolveCurrentUser = async () => {
  const email = getCurrentUserEmail();
  if (!email) return null;
  const ams = await fetchAMs();
  const match = ams.find(am => (am.email || "").toLowerCase() === email.toLowerCase());
  if (match) {
    // Cache the resolved name to legacy keys so older code paths keep working
    localStorage.setItem("abs_current_user", match.name);
    localStorage.setItem("abs_current_user_email", match.email);
    localStorage.setItem("abs_prepared_by", match.name);
    localStorage.setItem("abs_prepared_by_email", match.email);
    return { name: match.name, email: match.email, isAM: true, id: match.id };
  }
  // User is signed in but not an AM — still return their email so we can
  // show their name/email elsewhere (they just won't be auto-filled as AM)
  return { name: "", email, isAM: false };
};

// Synchronous helper — returns cached name if previously resolved, else empty.
export const getCurrentUserNameSync = () => localStorage.getItem("abs_current_user") || "";
export const getCurrentUserEmailSync = () => localStorage.getItem("abs_current_user_email") || getCurrentUserEmail();
